import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Toolbar,
  Paper,
  Popover,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import FlowBotVariablesPanel from '@/pages/atendimento/FlowBuilder/FlowBotVariablesPanel';
import { 
  FormatBold, 
  FormatItalic, 
  EmojiEmotions,
  Visibility,
  VisibilityOff,
  AutoAwesome,
  Add
} from '@mui/icons-material';
import { env } from '@/utils/env';

interface SysEditorWhatsappProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  showPreview?: boolean;
  rows?: number;
  label?: string;
  helperText?: string;
  showVariables?: boolean;
  variables?: Array<{ name: string; value: string }>;
}

const SysEditorWhatsapp: React.FC<SysEditorWhatsappProps> = ({
  value,
  onChange,
  placeholder = "Digite sua mensagem aqui...",
  maxLength = 1000,
  disabled = false,
  showPreview = true,
  rows = 4,
  label,
  helperText,
  showVariables = false,
  variables = [],
}) => {
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const [textFieldRef, setTextFieldRef] = useState<HTMLTextAreaElement | null>(null);
  const [internalShowPreview, setInternalShowPreview] = useState(false);
  
  // Estados para IA
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Funções do editor de texto
  const insertTextAtCursor = (textToInsert: string, wrapText?: { start: string; end: string }) => {
    if (!textFieldRef) return;

    const start = textFieldRef.selectionStart || 0;
    const end = textFieldRef.selectionEnd || 0;
    const selectedText = value.substring(start, end);
    
    let newText = '';
    
    if (wrapText) {
      // Para formatação (negrito, itálico)
      newText = value.substring(0, start) + 
                wrapText.start + selectedText + wrapText.end + 
                value.substring(end);
      
      // Definir nova posição do cursor
      setTimeout(() => {
        if (textFieldRef) {
          const newCursorPos = selectedText ? 
            start + wrapText.start.length + selectedText.length + wrapText.end.length :
            start + wrapText.start.length;
          textFieldRef.setSelectionRange(newCursorPos, newCursorPos);
          textFieldRef.focus();
        }
      }, 0);
    } else {
      // Para emoticons
      newText = value.substring(0, start) + textToInsert + value.substring(end);
      
      // Definir nova posição do cursor
      setTimeout(() => {
        if (textFieldRef) {
          const newCursorPos = start + textToInsert.length;
          textFieldRef.setSelectionRange(newCursorPos, newCursorPos);
          textFieldRef.focus();
        }
      }, 0);
    }
    
    onChange(newText);
  };

  const handleBold = () => {
    insertTextAtCursor('', { start: '*', end: '*' });
  };

  const handleItalic = () => {
    insertTextAtCursor('', { start: '_', end: '_' });
  };

  const handleEmojiClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji: string) => {
    insertTextAtCursor(` ${emoji} `);
    handleEmojiClose();
  };

  // Funções de IA
  const handleAiModalOpen = () => {
    setIsAiModalOpen(true);
    setAiPrompt('');
    setAiError(null);
  };

  const handleAiModalClose = () => {
    setIsAiModalOpen(false);
    setAiPrompt('');
    setAiResponse('');
    setAiError(null);
    setIsAiLoading(false);
  };

  const handleInsertAiResponse = () => {
    if (aiResponse.trim()) {
      // Inserir o texto da resposta no campo
      onChange(value + (value ? '\n\n' : '') + aiResponse);
      handleAiModalClose();
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Por favor, digite um prompt para gerar o texto.');
      return;
    }

    setIsAiLoading(true);
    setAiError(null);

    try {
      // Obter token de autenticação
      const token = localStorage.getItem('sysup_token');
      
      if (!token) {
        setAiError('Você precisa estar logado para usar a IA.');
        setIsAiLoading(false);
        return;
      }

      console.log('🤖 Gerando texto com IA...');
      console.log('📝 Prompt:', aiPrompt);

      // Chamada para a API do backend
      const response = await fetch(`${env.API_URL}/api/openai/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          maxTokens: 500,
          temperature: 0.7
        }),
      });

      console.log('📡 Status da resposta:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Erro da API:', errorData);
        
        if (response.status === 401) {
          setAiError('Sessão expirada. Faça login novamente.');
        } else if (errorData.code === 'OPENAI_NOT_CONFIGURED') {
          setAiError('Serviço de IA não configurado. Entre em contato com o administrador para configurar o token da OpenAI.');
        } else {
          setAiError(errorData.error || 'Erro ao gerar texto com IA');
        }
        return;
      }

      const data = await response.json();
      console.log('✅ Resposta da IA:', data);
      
      const generatedText = data.text || '';

      if (generatedText) {
        // Colocar o texto gerado no campo de resposta
        setAiResponse(generatedText);
        
        // Mostrar aviso se estiver usando fallback
        if (data.source === 'fallback') {
          console.warn('⚠️ Usando resposta simulada:', data.warning);
        } else {
          console.log('🎉 Texto gerado com sucesso pela OpenAI!');
        }
      } else {
        setAiError('Nenhum texto foi gerado. Tente novamente.');
      }
      
    } catch (error) {
      console.error('❌ Erro ao gerar texto:', error);
      setAiError('Erro inesperado. Verifique sua conexão e tente novamente.');
    } finally {
      setIsAiLoading(false);
    }
  };

  // Lista de emoticons populares
  const popularEmojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
    '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
    '😘', '😗', '😙', '😚', '🤗', '🤩', '🤔', '🤭',
    '🤫', '🤐', '😐', '😑', '😶', '😏', '😒', '🙄',
    '😬', '🤥', '😔', '😕', '🙁', '😖', '😣', '😞',
    '😓', '😩', '😫', '🥱', '😤', '😡', '🤬', '😠',
    '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥',
    '😢', '😭', '😤', '😮', '😦', '😧', '😯', '😲',
    '🥳', '🤪', '😜', '😝', '😛', '🤤', '😴', '😷',
    '🤒', '🤕', '🤢', '🤮', '🤧', '🥴', '😵', '🤠',
    '🤡', '🥸', '😎', '🤓', '🧐', '😕', '😟', '🙁',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙',
    '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️',
    '🖖', '👋', '🤝', '👏', '🙌', '👐', '🤲', '🤜',
    '🤛', '✊', '👊', '🤘', '💪', '🦾', '🖕', '✍️',
    '🙏', '🦶', '🦵', '🦿', '💄', '💋', '👄', '🦷',
    '👅', '👂', '🦻', '👃', '👣', '👁️', '👀', '🧠',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
    '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖',
    '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️',
    '🔥', '💯', '💢', '💥', '💫', '💦', '💨', '🕳️',
    '💬', '👁️‍🗨️', '🗨️', '🗯️', '💭', '💤', '👋', '🤚',
    '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
    '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇',
    '☝️', '👍', '👎', '👊', '✊', '🤛', '🤜', '👏',
    '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳'
  ];

  // Função para renderizar texto formatado no preview
  const renderFormattedText = (text: string) => {
    if (!text) return <span style={{ color: '#999' }}>Digite uma mensagem para ver o preview...</span>;

    // Função auxiliar para processar uma linha
    const processLine = (line: string, key: number) => {
      const elements: React.ReactNode[] = [];
      let lastIndex = 0;

      // Regex para encontrar formatação combinada
      const formatRegex = /(\*([^*]+)\*)|(_([^_]+)_)/g;
      let match;

      while ((match = formatRegex.exec(line)) !== null) {
        // Adicionar texto antes do match
        if (match.index > lastIndex) {
          elements.push(line.substring(lastIndex, match.index));
        }

        // Verificar se é negrito ou itálico
        if (match[1] && match[1].startsWith('*')) {
          // Negrito
          elements.push(<strong key={`bold-${match.index}`}>{match[2]}</strong>);
        } else if (match[3] && match[3].startsWith('_')) {
          // Itálico
          elements.push(<em key={`italic-${match.index}`}>{match[4]}</em>);
        }

        lastIndex = match.index + match[0].length;
      }

      // Adicionar texto restante
      if (lastIndex < line.length) {
        elements.push(line.substring(lastIndex));
      }

      // Se não houve formatação, retornar texto normal
      if (elements.length === 0) {
        elements.push(line);
      }

      return (
        <div key={key}>
          {elements.map((element, index) => (
            <React.Fragment key={index}>{element}</React.Fragment>
          ))}
        </div>
      );
    };

    // Quebrar o texto em linhas e processar cada uma
    const lines = text.split('\n');
    return (
      <div>
        {lines.map((line, index) => processLine(line, index))}
      </div>
    );
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}
      
      {/* Barra de ferramentas */}
      <Paper variant="outlined" sx={{ mb: 1 }}>
        <Toolbar 
          variant="dense" 
          sx={{ 
            minHeight: 40,
            gap: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <IconButton
            size="small"
            onClick={handleBold}
            disabled={disabled}
            title="Negrito (*texto*)"
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <FormatBold fontSize="small" />
          </IconButton>
          
          <IconButton
            size="small"
            onClick={handleItalic}
            disabled={disabled}
            title="Itálico (_texto_)"
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <FormatItalic fontSize="small" />
          </IconButton>
          
          <IconButton
            size="small"
            onClick={handleEmojiClick}
            disabled={disabled}
            title="Emoticons"
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <EmojiEmotions fontSize="small" />
          </IconButton>
          
          <IconButton
            size="small"
            onClick={handleAiModalOpen}
            disabled={disabled}
            title="Gerar texto com IA"
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <AutoAwesome fontSize="small" />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {showPreview && (
            <IconButton
              size="small"
              onClick={() => setInternalShowPreview(!internalShowPreview)}
              disabled={disabled}
              title={internalShowPreview ? "Ocultar Preview" : "Mostrar Preview"}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: internalShowPreview ? 'action.selected' : 'transparent',
                '&:hover': {
                  backgroundColor: internalShowPreview ? 'action.selected' : 'action.hover'
                }
              }}
            >
              {internalShowPreview ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          )}
          
          <Typography variant="caption" color="text.secondary">
            {value.length}/{maxLength}
          </Typography>
        </Toolbar>
        
        {/* Campo de texto */}
        <TextField
          fullWidth
          multiline
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          inputProps={{
            maxLength: maxLength,
            ref: (ref: HTMLTextAreaElement) => setTextFieldRef(ref),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />
      </Paper>

      {showVariables && variables.length > 0 && (
        <FlowBotVariablesPanel
          variables={variables}
          onInsert={(variableValue) => insertTextAtCursor(variableValue)}
        />
      )}

      {/* Preview da mensagem formatada */}
      {showPreview && internalShowPreview && value && (
        <Paper 
          variant="outlined" 
          sx={{ 
            mt: 2, 
            p: 2,
            backgroundColor: 'grey.50',
            borderColor: 'primary.main',
            borderWidth: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Visibility color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="primary" fontWeight="bold">
              Preview da Mensagem
            </Typography>
          </Box>
          <Box 
            sx={{ 
              minHeight: 40,
              p: 1,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              '& strong': {
                fontWeight: 'bold'
              },
              '& em': {
                fontStyle: 'italic'
              }
            }}
          >
            {renderFormattedText(value)}
          </Box>
        </Paper>
      )}
      
      {/* Helper text */}
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
      
      {/* Popover de Emoticons */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selecione um emoticon:
          </Typography>
          <Grid container spacing={0.5}>
            {popularEmojis.map((emoji, index) => (
              <Grid item key={index}>
                <IconButton
                  size="small"
                  onClick={() => handleEmojiSelect(emoji)}
                  sx={{
                    fontSize: '1.2rem',
                    width: 32,
                    height: 32,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.2)',
                    },
                  }}
                >
                  {emoji}
                </IconButton>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>
      
      {/* Modal de IA */}
      <Dialog open={isAiModalOpen} onClose={handleAiModalClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome color="secondary" />
            Gerar Texto com IA para WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent>
          {aiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {aiError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Campo do Prompt */}
            <Box sx={{ flex: 1 }}>
              <TextField
                autoFocus
                label="Descreva o que você quer que a IA escreva"
                placeholder="Ex: Escreva uma mensagem de boas-vindas para novos clientes"
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                disabled={isAiLoading}
                sx={{ mb: 1 }}
              />
              <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                💡 Dicas: Seja específico sobre o tom, estilo e conteúdo desejado para WhatsApp.
              </Box>
            </Box>

            {/* Campo da Resposta */}
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Resposta da IA"
                placeholder="A resposta da IA aparecerá aqui..."
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                value={aiResponse}
                onChange={(e) => setAiResponse(e.target.value)}
                disabled={isAiLoading}
                sx={{ mb: 1 }}
                InputProps={{
                  readOnly: false, // Permite edição da resposta
                }}
              />
              <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                ✏️ Você pode editar a resposta antes de inserir na mensagem.
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAiModalClose} disabled={isAiLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAiGenerate} 
            variant="outlined"
            disabled={isAiLoading || !aiPrompt.trim()}
            startIcon={isAiLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
          >
            {isAiLoading ? 'Gerando...' : 'Gerar Texto'}
          </Button>
          <Button 
            onClick={handleInsertAiResponse} 
            variant="contained" 
            disabled={!aiResponse.trim() || isAiLoading}
            startIcon={<Add />}
          >
            Inserir na Mensagem
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SysEditorWhatsapp; 
